import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';

import {
  AssignmentOptimizerSetting,
  ConfigurableFieldsEnum,
  ShiftCategoriesColorCode,
  ShiftCategory,
} from '@wilson/interfaces';
import { ShiftDropdownOptionsService } from '@wilson/non-domain-specific/shift-helpers/core';
import { QualificationCategorySelectComponent } from '@wilson/qualification-v2';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { Observable } from 'rxjs';
import { cloneDeep } from 'lodash';
export enum AssignmentOptionsFormEnum {
  ALGORITHM_ID = 'id',
}
export interface AssignmentsAlgorithmFormValue {
  [AssignmentOptionsFormEnum.ALGORITHM_ID]: string | null;
  [ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS]: boolean | null;
  [ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES]:
    | number
    | null;
  [ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES]: number | null;
  [ConfigurableFieldsEnum.IGNORED_QUALIFICATION_CATEGORY_IDS]: string[] | null;
  [ConfigurableFieldsEnum.REASSIGN_ALL]: boolean | null;
  [ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS]?: string[] | null;
}

export type AssignmentsAlgorithmFormControls = {
  [Property in keyof AssignmentsAlgorithmFormValue]: FormControl<
    AssignmentsAlgorithmFormValue[Property]
  >;
};

export type AssignmentsAlgorithmForm =
  FormGroup<AssignmentsAlgorithmFormControls>;

@Component({
  selector: 'wilson-assignment-optimizer-form',
  imports: [
    CommonModule,
    TranslateModule,
    NzButtonModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzInputNumberModule,
    NzSwitchModule,
    FormsModule,
    QualificationCategorySelectComponent,
  ],
  templateUrl: './assignment-optimizer-form.component.html',
  styleUrls: ['./assignment-optimizer-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentOptimizerFormComponent
  implements OnChanges, AfterViewInit
{
  private readonly shiftDropdownOptionsService = inject(
    ShiftDropdownOptionsService,
  );
  protected translate = inject(TranslateService);
  protected nzMessageService = inject(NzMessageService);

  @Input({ required: true })
  optimizerSettings:
    | (AssignmentOptimizerSetting & {
        translatedName: string;
        translatedDescription: string;
      })[]
    | null = null;

  @Output()
  updateFormOutput = new EventEmitter<Partial<AssignmentOptimizerSetting>>();

  protected formControls: Partial<AssignmentsAlgorithmFormControls> = {
    [AssignmentOptionsFormEnum.ALGORITHM_ID]: new FormControl(''),
  };

  protected ShiftCategoriesColorCode = ShiftCategoriesColorCode;
  protected form = new FormGroup(this.formControls);
  protected showFixedShiftsFieldSignal = signal<boolean>(false);
  protected ConfigurableFieldsEnum = ConfigurableFieldsEnum;
  protected shiftCategories$ = this.shiftDropdownOptionsService
    .shiftCategories$ as Observable<(ShiftCategory & { id: string })[]>;
  protected AssignmentOptionsFormEnum = AssignmentOptionsFormEnum;

  private lastEmittedValues: Partial<AssignmentsAlgorithmFormValue> | null =
    null;
  protected selectedSettingDescription = '';
  protected selectedAlgoSettingsConfigurableFieldsSignal = signal<
    Record<ConfigurableFieldsEnum, boolean>
  >({} as Record<ConfigurableFieldsEnum, boolean>);

  @ViewChild('minimalRestInput', { static: false, read: ElementRef })
  minimalRestInput!: ElementRef;

  @ViewChild('maximalTransferInput', { static: false, read: ElementRef })
  maximalTransferInput!: ElementRef;

  ngAfterViewInit(): void {
    const minimalRestInput =
      this.minimalRestInput.nativeElement.querySelector('input');

    const maximalTransferInput =
      this.maximalTransferInput.nativeElement.querySelector('input');

    minimalRestInput.addEventListener('blur', () => {
      this.updateFormValue();
    });

    maximalTransferInput.addEventListener('blur', () => {
      this.updateFormValue();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['optimizerSettings']) {
      if (this.optimizerSettings && this.optimizerSettings.length) {
        const formValues = this.getSelectedOptimizerSettingsFormValues(
          this.optimizerSettings,
        );
        this.form.patchValue(formValues);

        this.toggleFixedShiftCategoriesControl(
          !!formValues[ConfigurableFieldsEnum.REASSIGN_ALL],
          formValues[ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS] || [],
        );
      }
    }
  }

  protected onReassignAllChange(value: boolean): void {
    this.formControls[ConfigurableFieldsEnum.REASSIGN_ALL]?.setValue(value);
    this.form.markAsDirty();
    this.updateFormValue();

    this.toggleFixedShiftCategoriesControl(value, []);
  }

  protected changeAlgoVersionAndUpdateFormValue(algorithmId: string): void {
    if (this.optimizerSettings) {
      const duplicateSettings = cloneDeep(this.optimizerSettings);
      const selectedSetting = duplicateSettings.find(
        (setting) => setting.id === algorithmId,
      );
      if (selectedSetting) {
        const selectionState = {
          id: selectedSetting.id,
          isSelected: true,
        };
        this.updateFormOutput.emit(
          selectionState as Partial<AssignmentOptimizerSetting>,
        );
      } else {
        this.nzMessageService.error(this.translate.instant('general.error'));
      }
    }
  }

  protected updateFormValue(): void {
    const currentValues = { ...this.form.value };
    if (!this.form.pristine) {
      const valuesWithFixedShiftCategories: Partial<AssignmentsAlgorithmFormValue> =
        {
          ...currentValues,
          ...this.transformFieldTimeValues(currentValues),
        };
      if (
        this.formControls[ConfigurableFieldsEnum.REASSIGN_ALL]?.value === false
      ) {
        valuesWithFixedShiftCategories[
          ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS
        ] = [];
      }

      // This field is called "blockTransferDuringNightHours" but in UI we show it as "Allow night transfers" thus we need to invert the value before sending it to the backend
      if (
        this.selectedAlgoSettingsConfigurableFieldsSignal()[
          ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS
        ]
      ) {
        valuesWithFixedShiftCategories[
          ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS
        ] =
          !valuesWithFixedShiftCategories[
            ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS
          ];
      }
      if (
        this.haveValuesChanged(
          this.lastEmittedValues,
          valuesWithFixedShiftCategories,
        )
      ) {
        this.lastEmittedValues = { ...valuesWithFixedShiftCategories };
        this.updateFormOutput.emit(
          valuesWithFixedShiftCategories as Partial<AssignmentOptimizerSetting>,
        );
      }
    }
  }

  private transformFieldTimeValues(
    currentValues: Partial<AssignmentsAlgorithmFormValue>,
  ): Partial<AssignmentsAlgorithmFormValue> {
    const transformedValues: Partial<AssignmentsAlgorithmFormValue> = {};
    const configurableFields =
      this.selectedAlgoSettingsConfigurableFieldsSignal();

    if (
      configurableFields[
        ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES
      ]
    ) {
      const minRestTimeMinutes =
        currentValues[
          ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES
        ];
      transformedValues[
        ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES
      ] = minRestTimeMinutes ? minRestTimeMinutes * 60 : 0;
    }

    if (
      configurableFields[
        ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES
      ]
    ) {
      const maxTransferMinutes =
        currentValues[
          ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES
        ];
      transformedValues[
        ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES
      ] = maxTransferMinutes ? maxTransferMinutes * 60 : 0;
    }

    return transformedValues;
  }

  private haveValuesChanged(
    previous: Partial<AssignmentsAlgorithmFormValue> | null,
    current: Partial<AssignmentsAlgorithmFormValue>,
  ): boolean {
    if (!previous) {
      return true;
    } else {
      return Object.keys(current).some((key) => {
        const typedKey = key as keyof AssignmentsAlgorithmFormValue;
        const prevValue = previous[typedKey];
        const currValue = current[typedKey];

        if (Array.isArray(prevValue) && Array.isArray(currValue)) {
          if (prevValue.length !== currValue.length) return true;
          return !prevValue.every((val, index) => val === currValue[index]);
        }

        return prevValue !== currValue;
      });
    }
  }

  private toggleFixedShiftCategoriesControl(
    reassignAll: boolean,
    fixedShiftCategoryIds: string[],
  ): void {
    this.showFixedShiftsFieldSignal.set(reassignAll);
    if (reassignAll) {
      this.form.addControl(
        ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS,
        new FormControl<string[]>(fixedShiftCategoryIds || []),
      );
    } else {
      this.form.removeControl(ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS);
    }
  }

  private getSelectedOptimizerSettingsFormValues(
    settings: (AssignmentOptimizerSetting & {
      translatedName: string;
      translatedDescription: string;
    })[],
  ): Partial<AssignmentsAlgorithmFormValue> {
    const selectedAlgoSettings = settings.find(
      (setting) => setting.isSelected === true,
    );

    if (selectedAlgoSettings) {
      this.selectedSettingDescription =
        selectedAlgoSettings.translatedDescription;
      const fieldsMap = selectedAlgoSettings.configurableFields.reduce(
        (acc, field) => {
          acc[field] = true;
          return acc;
        },
        {} as Record<ConfigurableFieldsEnum, boolean>,
      );
      this.selectedAlgoSettingsConfigurableFieldsSignal.set(fieldsMap);
      const formValues: Partial<AssignmentsAlgorithmFormValue> = {
        [AssignmentOptionsFormEnum.ALGORITHM_ID]:
          selectedAlgoSettings.id || null,
      };

      // This field is called "blockTransferDuringNightHours" but in UI we show it as "Allow night transfers" thus we need to store value as inverted
      if (fieldsMap[ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS]) {
        this.form.addControl(
          ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS,
          new FormControl<boolean>(false),
        );
        formValues[ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS] =
          !selectedAlgoSettings[
            ConfigurableFieldsEnum.BLOCK_TRANSFER_DURING_NIGHT_HOURS
          ];
      }

      if (
        fieldsMap[
          ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES
        ]
      ) {
        this.form.addControl(
          ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES,
          new FormControl<number | null>(0),
        );
        const minimalRestTimeBetweenShiftsMinutes =
          selectedAlgoSettings[
            ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES
          ];

        formValues[
          ConfigurableFieldsEnum.MINIMAL_REST_TIME_BETWEEN_SHIFTS_MINUTES
        ] = minimalRestTimeBetweenShiftsMinutes
          ? minimalRestTimeBetweenShiftsMinutes / 60
          : 0;
      }

      if (
        fieldsMap[ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES]
      ) {
        this.form.addControl(
          ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES,
          new FormControl<number | null>(0),
        );
        const maximalLengthOfTransferMinutes =
          selectedAlgoSettings[
            ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES
          ];
        formValues[ConfigurableFieldsEnum.MAXIMAL_LENGTH_OF_TRANSFER_MINUTES] =
          maximalLengthOfTransferMinutes
            ? maximalLengthOfTransferMinutes / 60
            : 0;
      }

      if (
        fieldsMap[ConfigurableFieldsEnum.IGNORED_QUALIFICATION_CATEGORY_IDS]
      ) {
        this.form.addControl(
          ConfigurableFieldsEnum.IGNORED_QUALIFICATION_CATEGORY_IDS,
          new FormControl<string[] | null>([]),
        );
        formValues[ConfigurableFieldsEnum.IGNORED_QUALIFICATION_CATEGORY_IDS] =
          selectedAlgoSettings[
            ConfigurableFieldsEnum.IGNORED_QUALIFICATION_CATEGORY_IDS
          ];
      }

      if (fieldsMap[ConfigurableFieldsEnum.REASSIGN_ALL]) {
        this.form.addControl(
          ConfigurableFieldsEnum.REASSIGN_ALL,
          new FormControl<boolean>(false),
        );
        formValues[ConfigurableFieldsEnum.REASSIGN_ALL] =
          selectedAlgoSettings[ConfigurableFieldsEnum.REASSIGN_ALL];
      }

      if (fieldsMap[ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS]) {
        const fixedShiftCategoryIds =
          selectedAlgoSettings[ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS];
        if (fixedShiftCategoryIds) {
          formValues[ConfigurableFieldsEnum.FIXED_SHIFT_CATEGORY_IDS] =
            fixedShiftCategoryIds;
        }
      }

      return formValues;
    } else {
      this.nzMessageService.error(this.translate.instant('general.error'));
      return {};
    }
  }
}
